import api from './api';

export const settlementService = {
  // Search pending due bills
  searchDueBills: async (params = {}) => {
    const response = await api.get('/settlements/due-bills', { params });
    return response.data;
  },

  // Get full due bill details including order lines and previous receipts
  getDueBillDetail: async (labno) => {
    const response = await api.get(`/settlements/due-bills/${labno}`);
    return response.data;
  },

  // Record due settlement payment (single mode)
  createSettlement: async (data) => {
    const response = await api.post('/settlements', data);
    return response.data;
  },

  // Record multi-mode split settlement payment
  createSplitSettlement: async (data) => {
    const response = await api.post('/settlements/split', data);
    return response.data;
  },

  // Apply after-bill discount
  applyAfterBillDiscount: async (data) => {
    const response = await api.post('/settlements/after-bill-discount', data);
    return response.data;
  },

  // Process full or partial refund
  processRefund: async (data) => {
    const response = await api.post('/settlements/refund', data);
    return response.data;
  },

  // Edit receipt payment mode/reference within time window
  editReceipt: async (data) => {
    const response = await api.put('/settlements/receipts/edit', data);
    return response.data;
  },

  // Get complete financial audit trail (payments, refunds, discounts, edits)
  getAuditTrail: async (labno) => {
    const response = await api.get(`/settlements/${labno}/audit-trail`);
    return response.data;
  },

  // Get order test line items and evaluated cancellation eligibility
  getCancellationEligibility: async (labno) => {
    const response = await api.get(`/settlements/${labno}/cancellation-eligibility`);
    return response.data;
  },

  // Execute atomic test cancellation and financial refund adjustment, or — if the
  // caller's cancellation authority doesn't cover every selected test — raise an
  // approval request instead (response.data.wasExecutedDirectly === false).
  cancelTests: async (data) => {
    const response = await api.post('/settlements/cancel-tests', data);
    return response.data;
  },

  // --------------------------------------------------------------------------
  // Test Cancellation Policy Engine (centre_cancellation_policy / approval inbox)
  // --------------------------------------------------------------------------

  getCancellationStageOptions: async () => {
    const response = await api.get('/settlements/cancellation-policy/stage-options');
    return response.data;
  },

  getCancellationPolicy: async (centreId) => {
    const response = await api.get('/settlements/cancellation-policy', { params: { centreId } });
    return response.data;
  },

  saveCancellationPolicy: async (data) => {
    const response = await api.post('/settlements/cancellation-policy', data);
    return response.data;
  },

  getCancellationApprovalInbox: async (params = {}) => {
    const response = await api.get('/settlements/cancellation-approvals/inbox', { params });
    return response.data;
  },

  getCancellationApprovalDetail: async (requestId) => {
    const response = await api.get(`/settlements/cancellation-approvals/${requestId}`);
    return response.data;
  },

  approveCancellationRequest: async (requestId, data) => {
    const response = await api.post(`/settlements/cancellation-approvals/${requestId}/approve`, data);
    return response.data;
  },

  rejectCancellationRequest: async (requestId, data) => {
    const response = await api.post(`/settlements/cancellation-approvals/${requestId}/reject`, data);
    return response.data;
  },

  // --------------------------------------------------------------------------
  // Financial Summary, EOD Settlement, Refund Workflow & Transaction Analysis
  // --------------------------------------------------------------------------

  // Get financial summary (top cards, payment modes, my settlement / consolidated)
  getFinancialSummary: async (params = {}) => {
    const response = await api.get('/settlements/financial-summary', { params });
    return response.data;
  },

  // Get detailed transactions analysis grid
  getDetailedTransactions: async (params = {}) => {
    const response = await api.get('/settlements/transactions', { params });
    return response.data;
  },

  // Receptionist submits End-of-Day cash handover & settlement
  submitEodSettlement: async (data) => {
    const response = await api.post('/settlements/eod-submit', data);
    return response.data;
  },

  // Accounts/Admin reconciles settlement
  reconcileSettlement: async (data) => {
    const response = await api.post('/settlements/reconcile', data);
    return response.data;
  },

  // Get refund requests with aging
  getRefundRequests: async (params = {}) => {
    const response = await api.get('/settlements/refunds/aging', { params });
    return response.data;
  },

  // Submit formal refund request
  createRefundRequest: async (data) => {
    const response = await api.post('/settlements/refunds/request', data);
    return response.data;
  },

  // Accounts/Admin approves refund request
  approveRefundRequest: async (data) => {
    const response = await api.post('/settlements/refunds/approve', data);
    return response.data;
  },

  // Accounts/Admin rejects refund request
  rejectRefundRequest: async (data) => {
    const response = await api.post('/settlements/refunds/reject', data);
    return response.data;
  },

  // Process approved refund
  processApprovedRefund: async (data) => {
    const response = await api.post('/settlements/refunds/process', data);
    return response.data;
  },

  // The refund / discount limits the Admin set for the logged-in person, and what is left of them today
  getMyAuthority: async () => {
    const response = await api.get('/settlements/my-authority');
    return response.data;
  },

  // Get receptionists lookup
  getReceptionists: async (params = {}) => {
    const response = await api.get('/settlements/receptionists', { params });
    return response.data;
  },
};

export default settlementService;
